import {
  BulkValidSystemsRequestType,
  CondenserType,
  LevelType,
  ValidDuctlessSystemsInputType
} from 'data/types';
import {
  buildConcealedDuctGeometry,
  getConcealedDuctOriginOverridePx
} from 'utils/concealed-duct-geometry';
import { getPinnedPassiveSpaceIds } from 'utils/concealed-duct-run-pinning';
import { getGridSpacesForLevel } from 'utils/grid';
import {
  catalogSelectedAirHandlerType,
  shouldRequireDuctedAirHandler
} from 'utils/requires-ducted-air-handler';
import {
  aggregateRequirementLevel,
  heatingWeightForSpace,
  requirementLoadWeight
} from 'utils/space-heating-weight';
import { findSpaceById } from 'utils/structure';

const attributeValue = (
  space: ReturnType<typeof findSpaceById>,
  code: string
): unknown => space?.attributes.find((attr) => attr.code === code)?.value;

function numericAttribute(
  space: ReturnType<typeof findSpaceById>,
  code: string
): number | null {
  if (!space) return null;
  const raw = space.attributes.find((attr) => attr.code === code)?.value;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// The published ValidDuctlessSystemsInputType (common 1.0.275) predates the
// per-zone requiresDuctedAirHandler flag.
// TODO(AHA-6784): drop once the @alpinehomeair/common publish lands.
type ValidSystemsZoneInput = ValidDuctlessSystemsInputType['zones'][number] & {
  requiresDuctedAirHandler?: true;
  // TODO: drop once @alpinehomeair/common publishes requiredHeatingBTU /
  // requiredCoolingBTU on ValidDuctlessSystemsInputSchema.
  requiredHeatingBTU?: number;
  requiredCoolingBTU?: number;
};

export type ValidSystemsInput = Omit<ValidDuctlessSystemsInputType, 'zones'> & {
  requiredHeatingBTU?: number;
  requiredCoolingBTU?: number;
  zones: ValidSystemsZoneInput[];
};

// The keys every valid-systems / air-handler-options body opens with, as the
// published contract types them (instanceUniqueId: string | undefined).
export type ValidSystemsRequestBase = Pick<
  BulkValidSystemsRequestType,
  'zipCode' | 'instanceUniqueId' | 'configurationUniqueId' | 'condensers'
>;

export type BuildValidSystemsInputContext = {
  structure: LevelType[];
  supplementalHeatingWeight: number;
  supplementalCoolingWeight: number;
  emitDuctedZoneFlags?: boolean;
  leftoverSpaceIds?: ReadonlySet<string>;
};

export const buildValidSystemsInput = (
  condenser: CondenserType,
  {
    structure,
    supplementalHeatingWeight,
    supplementalCoolingWeight = 0.5,
    emitDuctedZoneFlags = false,
    leftoverSpaceIds
  }: BuildValidSystemsInputContext
): ValidSystemsInput | null => {
  const zonesWithItems = condenser.zones.filter((z) => z.zoneItems.length > 0);
  if (zonesWithItems.length === 0) return null;

  const zoneWeightedBTU = (
    zone: (typeof zonesWithItems)[number],
    side: 'heating' | 'cooling',
    weightFor: (value: unknown) => number
  ): number =>
    zone.zoneItems.reduce((sum, item) => {
      const space = findSpaceById(structure, item.structureItemId);
      return (
        sum +
        Math.round(
          weightFor(attributeValue(space, side)) * (item.BTUs?.[side] ?? 0)
        )
      );
    }, 0);

  const zones = zonesWithItems.map((zone, index) => {
    const heatingBTU = zoneWeightedBTU(zone, 'heating', requirementLoadWeight);
    const coolingBTU = zoneWeightedBTU(zone, 'cooling', requirementLoadWeight);
    const requiredHeatingBTU = zoneWeightedBTU(zone, 'heating', (value) =>
      heatingWeightForSpace(value, supplementalHeatingWeight)
    );
    const requiredCoolingBTU = zoneWeightedBTU(zone, 'cooling', (value) =>
      heatingWeightForSpace(value, supplementalCoolingWeight)
    );
    const coolingRequirement = aggregateRequirementLevel(
      zone.zoneItems.map((item) =>
        attributeValue(
          findSpaceById(structure, item.structureItemId),
          'cooling'
        )
      )
    );
    const heatingRequirement = aggregateRequirementLevel(
      zone.zoneItems.map((item) =>
        attributeValue(
          findSpaceById(structure, item.structureItemId),
          'heating'
        )
      )
    );
    const selectedAirHandlerType = catalogSelectedAirHandlerType(
      zone.airHandler?.airHandlerTypeCode ?? null
    );

    const additionalSupplyRunsBySpaceId = new Map<
      string,
      NonNullable<(typeof zone.zoneItems)[number]['additionalSupplyRuns']>
    >();
    zone.zoneItems.forEach((item) => {
      if (item.additionalSupplyRuns && item.additionalSupplyRuns.length > 0) {
        additionalSupplyRunsBySpaceId.set(
          item.structureItemId,
          item.additionalSupplyRuns
        );
      }
    });

    const ductGeometry = buildConcealedDuctGeometry({
      structure,
      spaces: zone.zoneItems.map((item) => ({
        uniqueId: item.structureItemId,
        structureSpaceId: item.structureItemId,
        registerOverridePx: item.registerOverride ?? null,
        supplyRunPointsPx: item.supplyRunPoints ?? null
      })),
      getGridSpacesForLevel: (levelIndex) =>
        getGridSpacesForLevel(structure, levelIndex),
      originOverridePx: getConcealedDuctOriginOverridePx(zone),
      returnOverridePx: zone.returnOverride ?? null,
      returnRunPointsPx: zone.returnRunPoints ?? null,
      additionalSupplyRunsBySpaceId,
      additionalReturnRunsPx: zone.additionalReturnRuns ?? []
    });

    const pinnedPassiveSpaceIds = getPinnedPassiveSpaceIds(zone);

    return {
      name: zone.name,
      uniqueId: zone.uniqueId,
      zoneIndex: index,
      heatingBTU,
      coolingBTU,
      requiredHeatingBTU,
      requiredCoolingBTU,
      restrictedAirHandlerTypes: [],
      selectedAirHandlerType,
      ...(shouldRequireDuctedAirHandler({
        emitDuctedZoneFlags,
        condenserType: condenser.type,
        leftoverSpaceIds,
        zone
      })
        ? { requiresDuctedAirHandler: true as const }
        : {}),
      coolingRequirement,
      heatingRequirement,
      returnDuct: ductGeometry.returnDuct,
      returnDucts: ductGeometry.returnDuctRuns,
      spaces: zone.zoneItems.map((item) => {
        const space = findSpaceById(structure, item.structureItemId);
        const rawSquareFootage = Number(
          space?.attributes.find((attr) => attr.code === 'square_footage')
            ?.value
        );
        const squareFootage = Number.isFinite(rawSquareFootage)
          ? Math.round(rawSquareFootage)
          : 0;
        const wallHeightFt = numericAttribute(space, 'ceiling_height');
        const vaulted = numericAttribute(space, 'vaulted_height');
        const sloped = numericAttribute(space, 'sloped_height');
        const peakHeightFt = vaulted ?? sloped ?? wallHeightFt;
        return {
          name: item.name,
          uniqueId: item.structureItemId,
          heatingBTU: Math.round(
            requirementLoadWeight(attributeValue(space, 'heating')) *
              (item.BTUs?.heating ?? 0)
          ),
          coolingBTU: Math.round(
            requirementLoadWeight(attributeValue(space, 'cooling')) *
              (item.BTUs?.cooling ?? 0)
          ),
          squareFootage: squareFootage || null,
          restrictedAirHandlerTypes: [],
          wallHeightFt,
          peakHeightFt,
          supplyDuct:
            ductGeometry.supplyDuctsBySpaceId.get(item.structureItemId) ??
            undefined,
          supplyDucts:
            ductGeometry.supplyDuctRunsBySpaceId.get(item.structureItemId) ??
            undefined,
          ...(pinnedPassiveSpaceIds?.has(item.structureItemId)
            ? { servePassively: true }
            : {})
        };
      })
    };
  });

  return {
    name: condenser.name,
    uniqueId: condenser.uniqueId,
    heatingBTU: zones.reduce((sum, zone) => sum + zone.heatingBTU, 0),
    coolingBTU: zones.reduce((sum, zone) => sum + zone.coolingBTU, 0),
    requiredHeatingBTU: zones.reduce(
      (sum, zone) => sum + (zone.requiredHeatingBTU ?? 0),
      0
    ),
    requiredCoolingBTU: zones.reduce(
      (sum, zone) => sum + (zone.requiredCoolingBTU ?? 0),
      0
    ),
    zones
  };
};
